import { useState, useEffect, useRef } from "react";
import {Bell, X, Loader2, Check, ListFilter, Inbox, UserPlus, AtSign, Share2, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { useNotifications } from "@/hooks/use-notifications";
import { useNotificationsContext } from "@/contexts/notification/notificationContext";
import { CreatedBy, NotificationType } from "@/types/notification";
import { postWithAuth } from "@/lib/api-helpers";
import NotificationRenderer from "@/components/tailwind/notification/notificationRenderer";
import { AnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import { DropdownMenu, DropdownMenuSectionHeading, DropdownMenuIcons } from "@/components/tailwind/ui/dropdown-menu";
import type { DropdownMenuItemProps } from "@/components/tailwind/ui/dropdown-menu";

type NotificationFilter =
  | "all"
  | "unread"
  | "requests"
  | "mentions"
  | "shared"
  | "updates";

// Type-based filter buckets ("all"/"unread" are handled by read-state instead).
type TypeFilter = "requests" | "mentions" | "shared" | "updates";

const ICON = "h-4 w-4";

const FILTER_OPTIONS: {
  value: NotificationFilter;
  label: string;
  icon: React.ReactNode;
}[] = [
  { value: "all", label: "All notifications", icon: <Inbox className={ICON} /> },
  { value: "unread", label: "Unread", icon: <DropdownMenuIcons.MarkUnread /> },
  { value: "requests", label: "Access requests", icon: <UserPlus className={ICON} /> },
  { value: "mentions", label: "Mentions & comments", icon: <AtSign className={ICON} /> },
  { value: "shared", label: "Shares & invites", icon: <Share2 className={ICON} /> },
  { value: "updates", label: "Status updates", icon: <Bell className={ICON} /> },
];

const FILTER_GROUPS: Record<TypeFilter, NotificationType[]> = {
  requests: ["JOIN", "WORKAREA_JOIN_REQUEST"],
  mentions: ["MENTION", "COMMENT"],
  shared: ["SHARE", "ASSIGN", "WORKSPACE_INVITE", "WORKAREA_ACCESS_GRANTED", "PERMISSION_CHANGED"],
  updates: ["ACCEPT", "REJECT", "WORKAREA_JOIN_REJECTED"],
};

function matchesFilter(type: NotificationType, filter: NotificationFilter): boolean {
  // "all" and "unread" are not type-based — they're resolved at the call site.
  if (filter === "all" || filter === "unread") return true;
  return FILTER_GROUPS[filter].includes(type);
}

// Persist the selected filter per-device (like the theme).
const FILTER_STORAGE_KEY = "notif-filter";

function readStoredFilter(): NotificationFilter {
  if (typeof window === "undefined") return "all";
  const v = window.localStorage.getItem(FILTER_STORAGE_KEY);
  return FILTER_OPTIONS.some((o) => o.value === v)
    ? (v as NotificationFilter)
    : "all";
}

// Bottom of the app header (h-11 = 44px + offline banner / safe-area inset).
const HEADER_OFFSET_TOP =
  "calc(max(var(--offline-banner-height, 0px), var(--safe-top, 0px)) + 44px)";

export default function NotificationModal() {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading , setIsLoading] = useState<boolean>(true);

  const [isMarkingAsRead, setIsMarkingAsRead] = useState<boolean>(false);
  const [respondedMap, setRespondedMap] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<NotificationFilter>(readStoredFilter);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);

  // Drawer animation: `mounted` = in DOM (through exit), `shown` = slid in.
  const [mounted, setMounted] = useState<boolean>(false);
  const [shown, setShown] = useState<boolean>(false);

  const { notifications,fetchNotifications, unreadCount, setNotifications } = useNotificationsContext()
  const { decideJoinRequest } = useNotifications();

  const modalRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const filterTriggerRef = useRef<HTMLButtonElement | null>(null);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (isMarkingAsRead) {
        return;
      }
      const target = e.target as HTMLElement | null;
      // Ignore clicks whose target was removed mid-click (e.g. a filter item or
      // dismissed row) — not a real outside click.
      if (target && !target.isConnected) {
        return;
      }
      if (modalRef.current &&
          !modalRef.current.contains(target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(target as Node)
        ) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [open, isMarkingAsRead]);
  // Close on Esc key
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open]);

  const handleNotificationClick = async() => {
    setIsLoading(true)
    try{
      const response = await fetchNotifications()
    }
    catch(error){
      console.log(error)
    }
    finally{
      setIsLoading(false);
    }
  }

  const handleAccept = async (notificationId: string , sentTo: CreatedBy[], workspaceId: string) => {
    const type = 'ACCEPT'
    const message = 'Request to join workspace Accepted';
    try{
      const response = await postWithAuth('/api/notification/add' , {
        notificationId,
        sentTo,
        type,
        workspaceId,
        message
      })

      if ("error" in response || "message" in response) {
        return null;
      }

      setRespondedMap(prev => ({ ...prev, [notificationId]: true }));

      // push notification
      decideJoinRequest(response.notification)

      // setNotifications(prev => prev.map(n =>
      //   n._id === notificationId ? { ...n, read: true } : n
      // ));

      toast.success("Accepted Successfully");
    }
    catch(error){
      console.log(error)
    }
  };

  const handleReject = async (notificationId: string,  sentTo: CreatedBy[], workspaceId: string) => {
     const type = 'REJECT'
     const message = 'Request to join workspace Rejected';

    try{
      const response = await postWithAuth('/api/notification/add' , {
        notificationId,
        sentTo,
        type,
        workspaceId,
        message
      })

      if ("error" in response || "message" in response) {
        return null;
      }

      setRespondedMap(prev => ({ ...prev, [notificationId]: true }));

      // push notification
      decideJoinRequest(response.notification)

      // setNotifications(prev => prev.map(n =>
      //   n._id === notificationId ? { ...n, read: true } : n
      // ));
      toast.success("Rejected Request");
    }
    catch(error){
      console.log(error)
    }
    
  };

  // Mark as read — flips read state, keeps the item (use dismiss to remove).
  const handleMarkAsRead = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMarkingAsRead(true);
    try {
      setNotifications(prev =>
        prev.map(n => (n._id === notificationId ? { ...n, read: true } : n))
      );

      const response = await postWithAuth("/api/notification/read/one", {
        notificationId,
      });
      if ("error" in response || "message" in response) {
        return;
      }
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  // Dismiss / archive — removes the notification from the user's inbox.
  const handleDismiss = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMarkingAsRead(true);
    try {
      setNotifications(prev => prev.filter(n => n._id !== notificationId));

      const response = await postWithAuth("/api/notification/dismiss/one", {
        notificationId,
      });
      if ("error" in response || "message" in response) {
        return;
      }
    } catch (error) {
      console.error("Failed to dismiss notification", error);
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  const handleMarkAllAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (notifications.length === 0) return;
    setIsMarkingAsRead(true);
    try {
      // Flip every notification to read (kept in the list, not removed).
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

      const response = await postWithAuth("/api/notification/read/all", {});
      if ("error" in response || "message" in response) {
        return;
      }
    } catch (error) {
      console.error("Failed to mark all notifications as read", error);
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  const visibleNotifications = notifications.filter((n) => {
    if (filter === "unread") return !n.read;
    return matchesFilter(n.type, filter);
  });

  // Filter menu rows, rendered with the app's generic dropdown components.
  const filterItems: DropdownMenuItemProps[] = FILTER_OPTIONS.map((opt) => {
    const selected = filter === opt.value;
    return {
      id: opt.value,
      label: opt.label,
      icon: opt.icon,
      selected,
      rightElement: selected ? (
        <Check className="h-4 w-4 text-blue-500" />
      ) : undefined,
      onClick: () => {
        setFilter(opt.value);
        setFilterOpen(false);
      },
    };
  });

  // Reset the filter dropdown whenever the panel closes.
  useEffect(() => {
    if (!open) setFilterOpen(false);
  }, [open]);

  // Persist the selected filter so it survives reloads.
  useEffect(() => {
    try {
      window.localStorage.setItem(FILTER_STORAGE_KEY, filter);
    } catch {}
  }, [filter]);

  // Drive the slide-in / slide-out drawer animation.
  useEffect(() => {
    if (open) {
      setMounted(true);
      // Double rAF: paint off-screen (translate-x-full) first, then slide in,
      // so the transition animates instead of popping into place.
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    // Slide out, then unmount once the transition finishes.
    setShown(false);
    const timer = setTimeout(() => setMounted(false), 300);
    return () => clearTimeout(timer);
  }, [open]);

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          if(!open){
            console.log("in the notification call fuction ")
            handleNotificationClick();
          }
          setOpen(!open)
        }}
        className="relative p-1.5 rounded-md hover-component  transition-colors"
        aria-label="Notifications"
        title="Notifications"
      >
        <Inbox className="w-[18px] h-[18px]" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1.5 bg-red-400 font-medium rounded-full h-1.5 w-1.5 leading-none">
            {/* {unreadCount} */}
          </span>
        )}
      </button>

      {/* Notification drawer — slides in from the right, just below the header */}
      {mounted && (
        <div
          className="fixed left-0 right-0 bottom-0 z-50"
          style={{ top: HEADER_OFFSET_TOP }}
        >
          {/* Transparent click-catcher — closes on outside click, no dimming. */}
          <div onClick={() => setOpen(false)} className="absolute inset-0" />

          {/* Drawer panel */}
          <div
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            className={`absolute inset-y-0 right-0 flex h-full w-full max-w-full flex-col bg-modal border-l border-component shadow-2xl transform transition-transform duration-300 ease-out will-change-transform sm:w-80 md:w-[22rem] ${
              shown ? "translate-x-0" : "translate-x-full"
            }`}
          >

            {/* Header */}
            <div className="flex justify-between items-center px-3 py-2.5 border-b  border-component">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Notifications
              </h2>
              <div className="flex items-center gap-0.5">
                <button
                  onClick={(e) => handleMarkAllAsRead(e)}
                  disabled={unreadCount === 0}
                  title="Mark all as read"
                  className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover-component hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:text-gray-400  dark:hover:text-gray-200"
                >
                  <Check className="h-4 w-4" />
                </button>

                {/* Filter */}
                <button
                  ref={filterTriggerRef}
                  title="Filter notifications"
                  aria-expanded={filterOpen}
                  onClick={() => setFilterOpen(!filterOpen)}
                  className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors hover-component  ${
                    filter !== "all"
                      ? "text-blue-500 dark:text-blue-500"
                      : filterOpen
                        ? "text-gray-700 dark:text-gray-300"
                        : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  }`}
                >
                  <ListFilter className="h-4 w-4" />
                </button>
                {filterOpen && (
                  <AnchoredPopover
                    anchor={filterTriggerRef}
                    onClose={() => setFilterOpen(false)}
                    placement="bottom-end"
                    offsetPx={6}
                    className="z-[9999] w-56 rounded-lg border border-component bg-modal p-1 text-popover-foreground shadow-md outline-none"
                  >
                    <DropdownMenuSectionHeading className="px-2 pb-1 pt-1">
                      Filter
                    </DropdownMenuSectionHeading>
                    <DropdownMenu items={filterItems} className="p-0" />
                  </AnchoredPopover>
                )}

                <button
                  onClick={() => setOpen(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 transition-colors hover-component hover:text-gray-700 dark:text-gray-400  dark:hover:text-gray-200"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="clean-scrollbar flex-1 divide-y divide-gray-100 dark:divide-[rgb(37,37,37)] overflow-y-auto p-1 px-2">
              {isLoading ? (
                <div className="flex items-center justify-center p-10">
                  <Loader2 className="animate-spin w-6 h-6 text-gray-600 dark:text-gray-300" />
                </div>
              ) : visibleNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    {filter === "all"
                      ? "No notifications yet"
                      : "No notifications match this filter"}
                  </p>
                </div>
              ) : (
                visibleNotifications.map((n) => (
                  <div
                    key={n._id}
                    className="group relative rounded-lg py-1 transition-colors hover-component "
                  >
                    {/* Hover-revealed action toolbar */}
                    <div className="absolute right-2 top-2 z-10 flex items-center gap-0.5 rounded-md border  bg-white p-0.5 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 border-component dark:bg-[rgb(32,32,32)]">
                      {!n.read && (
                        <button
                          onClick={(e) => handleMarkAsRead(e, n._id)}
                          disabled={isMarkingAsRead}
                          title="Mark as read"
                          className="flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover-component hover:text-gray-700 disabled:opacity-50 dark:text-gray-400  dark:hover:text-gray-200"
                        >
                          {/* "mark as read" icon (box + checkmark) */}
                          <svg
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                            className="h-[18px] w-[18px]"
                            fill="currentColor"
                          >
                            <path d="M11.376 3.125c-.24.374-.336.818-.282 1.25H5.25a.875.875 0 0 0-.875.875v9.5c0 .483.392.875.875.875h9.5a.875.875 0 0 0 .875-.875V8.84a1.87 1.87 0 0 0 1.25-.711v6.621a2.125 2.125 0 0 1-2.125 2.125h-9.5a2.125 2.125 0 0 1-2.125-2.125v-9.5c0-1.174.951-2.125 2.125-2.125z" />
                            <path d="M18.51.57a.625.625 0 0 1 1.073.642l-3.656 6.094a.626.626 0 0 1-1.011.086l-2.437-2.844-.073-.105a.626.626 0 0 1 .93-.796l.092.086L15.3 5.918z" />
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={(e) => handleDismiss(e, n._id)}
                        disabled={isMarkingAsRead}
                        title="Delete"
                        className="flex h-6 w-6 items-center justify-center rounded text-gray-500 transition-colors hover-component hover:text-red-600 disabled:opacity-50 dark:text-gray-400  dark:hover:text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <NotificationRenderer
                      notification={n}
                      handleAccept={handleAccept}
                      handleReject={handleReject}
                      responded={!!respondedMap[n._id]}
                    />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}